import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageCircle,
  Heart,
  Eye,
  Bell,
  Coins,
  CreditCard,
  X,
  UserPlus,
  Shield,
  AlertTriangle,
  Activity,
  Flag,
  DollarSign
} from 'lucide-react';
import type { Notification } from '@/types/notification';
import { NotificationType } from '@/types/notification';
import { useNotifications } from '@/contexts/NotificationContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { resolveMediaUrl } from '@/lib/media';
import { formatRelativeTime, formatDateTime } from '@/lib/utils/time';
import { creatorSpaceApi } from '@/lib/api/creatorSpace';
import { HStack } from '@/components/core';

interface NotificationItemProps {
  notification: Notification;
  onClose?: () => void;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({ 
  notification, 
  onClose 
}) => {
  const navigate = useNavigate();
  const { markAsRead, deleteNotification } = useNotifications();

  // Check if this is an admin notification
  const isAdminNotification = [
    NotificationType.SYSTEM_ALERT,
    NotificationType.ADMIN_ACTIVITY,
    NotificationType.PERFORMANCE_WARNING,
    NotificationType.USER_REPORT,
    NotificationType.PAYMENT_ISSUE,
    NotificationType.CONTENT_FLAGGED
  ].includes(notification.type as any);

  const getIcon = () => {
    switch (notification.type) {
      case NotificationType.MESSAGE:
      case NotificationType.FOLLOW_UP:
        return <MessageCircle className="h-5 w-5" />;
      case NotificationType.PROFILE_VISIT:
        return <Eye className="h-5 w-5" />;
      case NotificationType.MATCH:
        return <UserPlus className="h-5 w-5" />;
      case NotificationType.LIKE:
        return <Heart className="h-5 w-5" />;
      case NotificationType.COIN_PURCHASE:
        return <Coins className="h-5 w-5" />;
      case NotificationType.SUBSCRIPTION:
        return <CreditCard className="h-5 w-5" />;
      // Admin notification types
      case NotificationType.SYSTEM_ALERT:
        return <Shield className="h-5 w-5" />;
      case NotificationType.ADMIN_ACTIVITY:
        return <Activity className="h-5 w-5" />;
      case NotificationType.PERFORMANCE_WARNING:
        return <AlertTriangle className="h-5 w-5" />;
      case NotificationType.USER_REPORT:
      case NotificationType.CONTENT_FLAGGED:
        return <Flag className="h-5 w-5" />;
      case NotificationType.PAYMENT_ISSUE:
        return <DollarSign className="h-5 w-5" />;
      default:
        return <Bell className="h-5 w-5" />;
    }
  };

  const getIconBgColor = () => {
    // Admin notifications get red background
    if (isAdminNotification) {
      return 'bg-error\/60 text-white';
    }

    switch (notification.type) {
      case NotificationType.MESSAGE:
      case NotificationType.FOLLOW_UP:
        return 'bg-info\/60 text-white';
      case NotificationType.PROFILE_VISIT:
        return 'bg-brand-secondary\/60 text-white';
      case NotificationType.MATCH:
        return 'bg-success\/60 text-white';
      case NotificationType.LIKE:
        return 'bg-brand-primary\/60 text-white';
      case NotificationType.COIN_PURCHASE:
        return 'bg-warning\/60 text-white';
      case NotificationType.SUBSCRIPTION:
        return 'bg-brand-secondary\/60 text-white';
      default:
        return 'bg-muted\/60 text-white';
    }
  };

  const handleClick = async () => {
    // Mark as read if not already
    if (!notification.is_read) {
      await markAsRead([notification.id]);
    }

    // Navigate based on notification type
    if (notification.type === NotificationType.PROFILE_VISIT) {
      // For profile visits: if creator info exists, prefer creator profile route
      const isCreator = Boolean(notification.data?.is_creator);
      const creatorId = notification.data?.creator_id;
      const characterId = notification.data?.character_id;

      if (isCreator && creatorId) {
        navigate(`/creator/${creatorId}`);
      } else if (characterId) {
        // Try to resolve creator by characterId as a safe fallback
        try {
          // Robust: paginate in chunks of 100 (backend limit) to find matching creator
          const findCreatorIdByCharacter = async (cid: number): Promise<number | null> => {
            let skip = 0;
            const limit = 100;
            for (let i = 0; i < 10; i++) { // cap to 1000 just in case
              const page = await creatorSpaceApi.getCreators(skip, limit);
              const arr = Array.isArray(page) ? page : [];
              if (arr.length === 0) return null;
              const found = arr.find((c: any) => c?.character_id === cid);
              if (found?.id) return found.id;
              skip += limit;
            }
            return null;
          };
          const maybeId = await findCreatorIdByCharacter(Number(characterId));
          if (maybeId) navigate(`/creator/${maybeId}`); else navigate(`/characters/${characterId}`);
        } catch {
          navigate(`/characters/${characterId}`);
        }
      } else {
        // Fallback to profile visits page if no identifiers
        navigate('/profile/visits');
      }
    } else if (notification.data) {
      if (notification.data.session_id && notification.data.character_id) {
        // For messages, navigate to the chat
        navigate(`/chats/${notification.data.character_id}?session=${notification.data.session_id}`);
      } else if (notification.type === NotificationType.MATCH && notification.data.character_id) {
        // For matches, navigate to character to start chatting
        navigate(`/characters/${notification.data.character_id}`);
      } else if (notification.data.character_id) {
        // For likes or other character-related notifications
        navigate(`/characters/${notification.data.character_id}`);
      } else if (notification.data.match_id) {
        // For matches without character_id (fallback)
        navigate('/matches');
      } else if (notification.type === NotificationType.COIN_PURCHASE ||
                 notification.type === NotificationType.SUBSCRIPTION) {
        // For billing-related notifications
        navigate('/billing');
      }
    }

    onClose?.();
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteNotification(notification.id);
  };


  return (
    <HStack
      align="start"
      gap="sm"
      className={cn(
        "design-card p-token-md transition-all duration-200 group",
        "hover:bg-white/10 cursor-pointer",
        !notification.is_read && "ring-1 ring-brand-primary/50 bg-white/5",
        isAdminNotification && "bg-error/10 hover:bg-error/15 ring-1 ring-error/30",
        isAdminNotification && !notification.is_read && "ring-error/50"
      )}
      onClick={handleClick}
    >
      {/* Character Image or Icon */}
      <div className="shrink-0">
        {notification.data?.character_image ? (
          <div className="relative w-10 h-10">
            <div className="w-full h-full rounded-full overflow-hidden ring-2 ring-white/20">
              <img
                src={resolveMediaUrl(notification.data.character_image)}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div className={cn(
              "absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center",
              getIconBgColor()
            )}>
              {React.cloneElement(getIcon(), { className: "h-3 w-3" })}
            </div>
          </div>
        ) : (
          <div className={cn(
            "w-10 h-10 rounded-full flex items-center justify-center",
            getIconBgColor()
          )}>
            {getIcon()}
          </div>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <p className={cn(
          "text-sm design-text-primary",
          !notification.is_read && "font-semibold"
        )}>
          {notification.title}
        </p>
        <p className="text-sm design-text-secondary line-clamp-2 mt-0.5">
          {notification.content}
        </p>
      </div>

      {/* Time - right-aligned and top-aligned */}
      <span
        className="text-xs design-text-secondary shrink-0 whitespace-nowrap"
        title={formatDateTime(new Date(notification.created_at))}
      >
        {formatRelativeTime(new Date(notification.created_at))}
      </span>

      <Button
        variant="ghost"
        size="icon"
        className="shrink-0 h-8 w-8 design-text-secondary hover:text-error opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={handleDelete}
      >
        <X className="h-4 w-4" />
      </Button>
    </HStack>
  );
};